import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import type { ScoreSnapshot, Action, Connection } from '../contract';
import {
  XIcon, SparklesIcon, CheckIcon, FileTextIcon, SearchIcon, MessageCircleQuestionIcon,
  CalendarClockIcon, PlugIcon,
} from 'lucide-react';
import { Chip } from '../primitives/Chip';
import { Button } from '../primitives/Button';
import { setActionStatus, makeTrackerScope } from '../trackerState';
import { cn } from '../lib/cn';

/**
 * The fix flow. Clicking "Fix" on a question or a to-do card opens this
 * slide-over. It shows what the fix changes, how ourteam (or you) does it, the
 * generated draft to review, and — after approval — the fulfillment timeline:
 * how the approved draft actually reaches the client's live property.
 */

interface DrawerCtx {
  openAction: (id: string) => void;
}
const Ctx = createContext<DrawerCtx>({ openAction: () => {} });
export const useActionDrawer = () => useContext(Ctx);

export function ActionDrawerProvider({
  snapshot,
  children,
}: {
  snapshot: ScoreSnapshot;
  children: ReactNode;
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  const action = openId ? snapshot.actions.find((a) => a.id === openId) ?? null : null;
  const ctx = useMemo(() => ({ openAction: setOpenId }), []);

  const helpedQuestions = action
    ? snapshot.questions.filter((q) => q.actionId === action.id).map((q) => q.text)
    : [];
  const connection = action?.channel
    ? snapshot.connections.find((c) => c.channel === action.channel) ?? null
    : null;

  return (
    <Ctx.Provider value={ctx}>
      {children}
      {action && (
        <ActionPanel
          key={action.id}
          action={action}
          scope={makeTrackerScope(snapshot.businessId, snapshot.capturedAt)}
          helpedQuestions={helpedQuestions}
          connection={connection}
          onClose={() => setOpenId(null)}
        />
      )}
    </Ctx.Provider>
  );
}

interface Step { label: string; note: ReactNode; state: 'done' | 'active' | 'upcoming'; cta?: string }

function fulfillmentSteps(action: Action, connection: Connection | null): Step[] {
  if (action.owner === 'client') {
    return [
      { label: 'You marked this done', note: 'Just now', state: 'done' },
      { label: 'We confirm it', note: 'At your next weekly check we look for the change and log it', state: 'upcoming' },
    ];
  }

  const channelLabel = connection?.label ?? 'your site';
  const connected = !connection || connection.status === 'connected';

  if (!connected) {
    return [
      { label: 'You approved the draft', note: 'Just now', state: 'done' },
      {
        label: `Connect ${channelLabel} first`,
        note: connection?.method ?? 'We need access before we can publish for you',
        state: 'active',
        cta: connection?.status === 'action_needed' ? 'Finish setup' : `Connect ${channelLabel}`,
      },
      { label: `We publish it to ${channelLabel}`, note: 'Our team applies the approved draft', state: 'upcoming' },
      { label: 'Verified', note: 'Your next weekly check confirms it went live', state: 'upcoming' },
    ];
  }

  return [
    { label: 'You approved the draft', note: 'Just now', state: 'done' },
    { label: 'Queued for ourteam', note: 'We pick it up within a business day', state: 'active' },
    { label: `We publish it to ${channelLabel}`, note: connection?.method ?? 'Our team applies the approved draft directly', state: 'upcoming' },
    { label: 'Verified', note: 'Your next weekly check confirms it went live and starts measuring the lift', state: 'upcoming' },
  ];
}

function ActionPanel({
  action, scope, helpedQuestions, connection, onClose,
}: {
  action: Action;
  scope: string;
  helpedQuestions: string[];
  connection: Connection | null;
  onClose: () => void;
}) {
  const [submitted, setSubmitted] = useState(false);
  const oura = action.owner === 'oura';
  /**
   * A task Google proves and the AI engines do not (#3880).
   *
   * Explicit `["google"]` with no `"ai"` in it. The tracker carried no `helps`
   * at all before ourteam-backend#4983, so an absent or empty list falls
   * through to the AI sentence this drawer has always shown — the opposite
   * default would have every old task claiming a monthly Google reading nobody
   * takes for it.
   */
  const googleOnly = Array.isArray(action.helps)
    && action.helps.includes('google')
    && !action.helps.includes('ai');
  const steps = action.steps ?? [action.body];
  /**
   * The measured move, when Google has actually been read twice (#3880,
   * ourteam-backend#4984).
   *
   * BOTH SIDES OR NOTHING. `before` is frozen at task creation and `after` is
   * written by the monthly pull, so a task that has only ever been read once
   * says nothing about movement rather than implying a change from a single
   * reading. A query Google withholds later arrives with a null position —
   * unmeasured, not a drop.
   */
  const moved = typeof action.before?.position === 'number' && typeof action.after?.position === 'number'
    ? { from: Math.round(action.before.position), to: Math.round(action.after.position) }
    : null;

  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <aside className="absolute right-0 top-0 flex h-full w-full max-w-[620px] flex-col border-l border-gray-200 bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-7 py-5">
          <div className="min-w-0">
            <div className="mb-1.5 flex items-center gap-2">
              <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium', oura ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600')}>
                <span className={cn('flex h-4 w-4 items-center justify-center rounded-full text-[8px] font-bold text-white', oura ? 'bg-blue-600' : 'bg-gray-500')}>
                  {oura ? 'B' : 'Y'}
                </span>
                {oura ? 'ourteam does this' : 'You do this'}
              </span>
              {action.estLift !== null && (
                <Chip className="border-blue-200 bg-blue-50 text-blue-700">+{action.estLift} pts</Chip>
              )}
            </div>
            <h2 className="text-xl font-semibold leading-snug text-gray-900">{action.title}</h2>
          </div>
          <button onClick={onClose} className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700">
            <XIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        {submitted ? (
          <FulfillmentView action={action} connection={connection} />
        ) : (
          <div className="flex-1 space-y-7 overflow-y-auto px-7 py-6">
            <p className="text-[15px] leading-relaxed text-gray-600">{action.body}</p>

            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">What this fixes</h3>
              <div className="mb-2 flex flex-wrap gap-2">
                {action.helps.includes('ai') && (
                  <Chip className="border-blue-200 bg-blue-50 text-blue-700"><MessageCircleQuestionIcon className="mr-1 h-3 w-3" /> When people ask AI</Chip>
                )}
                {action.helps.includes('google') && (
                  <Chip className="border-emerald-200 bg-emerald-50 text-emerald-700"><SearchIcon className="mr-1 h-3 w-3" /> When people search Google</Chip>
                )}
              </div>
              {helpedQuestions.length > 0 && (
                <ul className="space-y-1">
                  {helpedQuestions.map((q) => (
                    <li key={q} className="flex items-start gap-2 text-sm text-gray-700">
                      <CheckIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
                      <span className="italic">"{q}"</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">{oura ? 'How ourteam does it' : 'How to do it'}</h3>
              <ol className="space-y-2.5">
                {steps.map((s, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">{i + 1}</span>
                    <span className="text-sm text-gray-700">{s}</span>
                  </li>
                ))}
              </ol>
            </section>

            {action.draftReady && action.draftPreview && (
              <section>
                <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-gray-400">
                  <FileTextIcon className="h-3.5 w-3.5" />
                  {action.draftCount && action.draftCount > 1 ? `${action.draftCount} drafts ready to review` : 'Draft ready to review'}
                </h3>
                <div className="whitespace-pre-wrap rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm leading-relaxed text-gray-700">
                  {action.draftPreview}
                </div>
              </section>
            )}

            <section className="flex items-start gap-3 rounded-lg bg-blue-50 p-4">
              <CalendarClockIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" />
              <div>
                <h3 className="text-sm font-semibold text-gray-900">When you'll see it move</h3>
                <p className="mt-1 text-sm text-gray-700">
                  {action.estLift !== null ? (
                    // A search-sourced fix is proven by Google's own rows, not by
                    // re-asking the AI engines, so it must not promise a check
                    // nobody runs for it (#3880, ourteam-backend#4983).
                    //
                    // `helps` reads `["google"]` only on those tasks. ABSENT
                    // MEANS AI: the tracker carried no `helps` before #4983, so
                    // an older task keeps the sentence it has always had rather
                    // than claiming a Google reading that never happened.
                    googleOnly ? (
                      <>Once this is live, we read Google&rsquo;s own numbers for these searches every month and confirm the real change at your next scorecard — that turns the estimated <b>+{action.estLift}</b> into a measured number.</>
                    ) : (
                      <>Once this is live, we re-check the AI engines every week and confirm the real change at your next monthly scorecard — that turns the estimated <b>+{action.estLift}</b> into a measured number.</>
                    )
                  ) : (
                    <>You are new to us, so we can't promise a number yet. Once this is live we'll watch it over the next few weekly checks and show the measured change when we're confident.</>
                  )}
                </p>
                {/* Google's own two readings, stated as they arrived. No
                    delta, no arrow-colour verdict: the two numbers are the
                    claim, and the source is named because it is the only
                    thing that took them. */}
                {moved && (
                  <p className="mt-2 text-sm text-gray-700">
                    Google has {action.source?.ref ? <>&ldquo;{action.source.ref}&rdquo;</> : 'this search'} at{' '}
                    <b className="tabular-nums">#{moved.to}</b> today, from{' '}
                    <b className="tabular-nums">#{moved.from}</b> when this task was created.
                  </p>
                )}
              </div>
            </section>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-gray-200 px-7 py-4">
          {submitted ? (
            <>
              <span className="text-xs text-gray-500">We'll keep you posted here and in your activity feed.</span>
              <Button variant="outline" onClick={onClose}>Close</Button>
            </>
          ) : (
            <>
              {oura ? (
                <Button
                  className="ml-auto"
                  onClick={() => {
                    setSubmitted(true);
                    // Performed in the platform: the board card moves to
                    // In progress on its own (done once ourteam verifies).
                    setActionStatus(scope, action.id, 'in_progress');
                  }}
                >
                  <SparklesIcon className="h-4 w-4" />
                  {action.draftReady ? 'Approve & publish' : 'Have ourteam do this'}
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  className="ml-auto"
                  onClick={() => {
                    setSubmitted(true);
                    setActionStatus(scope, action.id, 'done');
                  }}
                >
                  <CheckIcon className="h-4 w-4" />
                  Mark as done
                </Button>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

function FulfillmentView({ action, connection }: { action: Action; connection: Connection | null }) {
  const steps = fulfillmentSteps(action, connection);
  const oura = action.owner === 'oura';

  return (
    <div className="flex-1 overflow-y-auto px-7 py-6">
      <div className="mb-6 flex items-center gap-3 rounded-lg bg-emerald-50 p-4">
        <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white">
          <CheckIcon className="h-4 w-4" />
        </span>
        <div>
          <p className="text-sm font-semibold text-gray-900">
            {oura ? "Approved — ourteam is on it" : "Marked as done"}
          </p>
          <p className="text-sm text-gray-600">
            {oura ? "Here's exactly what happens next." : "We'll confirm the change on your next check."}
          </p>
        </div>
      </div>

      <ol className="relative">
        {steps.map((step, i) => {
          const last = i === steps.length - 1;
          return (
            <li key={i} className="relative flex gap-3.5 pb-6 last:pb-0">
              {!last && <span className="absolute left-[11px] top-6 h-full w-px bg-gray-200" />}
              <span
                className={cn(
                  'relative z-10 mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border-2',
                  step.state === 'done' && 'border-emerald-600 bg-emerald-600 text-white',
                  step.state === 'active' && 'border-blue-600 bg-white',
                  step.state === 'upcoming' && 'border-gray-300 bg-white',
                )}
              >
                {step.state === 'done' ? (
                  <CheckIcon className="h-3.5 w-3.5" />
                ) : step.state === 'active' ? (
                  <span className="h-2 w-2 rounded-full bg-blue-600" />
                ) : (
                  <span className="h-2 w-2 rounded-full bg-gray-300" />
                )}
              </span>
              <div className="min-w-0 flex-1">
                <p className={cn('text-sm font-semibold', step.state === 'upcoming' ? 'text-gray-500' : 'text-gray-900')}>
                  {step.label}
                </p>
                <p className="mt-0.5 text-sm text-gray-500">{step.note}</p>
                {step.cta && step.state === 'active' && (
                  <Button size="sm" className="mt-2">
                    <PlugIcon className="h-4 w-4" />
                    {step.cta}
                  </Button>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
